import { InputField } from 'components';
import React, { useEffect, useMemo, useState } from 'react';

const defaultNotificationState = {
    title: '',
    body: '',
    scheduled_notification: false,
    scheduled_date: '',
    scheduled_time: '',
};

const getLocalDateTimeStrings = (dateValue) => {
    if (!dateValue) return { date: '', time: '' };
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return { date: '', time: '' };
    const offsetDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
    const isoString = offsetDate.toISOString();
    return {
        date: isoString.slice(0, 10),
        time: isoString.slice(11, 16),
    };
};

const normaliseInitialState = (initialData) => {
    if (!initialData) return { ...defaultNotificationState };

    const scheduledSource = initialData.scheduledAt || initialData.scheduled_at;
    const isScheduled = initialData?.scheduled_notification ?? Boolean(scheduledSource);
    const baseState = {
        ...defaultNotificationState,
        title: initialData?.title ?? '',
        body: initialData?.body ?? '',
        scheduled_notification: isScheduled,
        scheduled_date: initialData?.scheduled_date ?? '',
        scheduled_time: initialData?.scheduled_time ?? '',
    };

    if (isScheduled) {
        if (!baseState.scheduled_date || !baseState.scheduled_time) {
            const { date, time } = getLocalDateTimeStrings(scheduledSource);
            baseState.scheduled_date = baseState.scheduled_date || date;
            baseState.scheduled_time = baseState.scheduled_time || time;
        }
    }

    return baseState;
};

function Index({
    handleSubmit,
    handleCancel,
    allUser = true,
    cancel = true,
    initialData = null,
    formTitle = 'Create Notification',
    submitButtonLabel = 'Submit',
    allUsersButtonLabel = 'Sent to All Users',
    disableScheduleToggle = false,
}) {
    const [notificationData, setNotificationData] = useState(() => normaliseInitialState(initialData));

    useEffect(() => {
        setNotificationData(normaliseInitialState(initialData));
    }, [initialData]);

    const content = useMemo(() => ([
        { label: 'Title', value: 'title', isTextArea: false },
        { label: 'Body', value: 'body', isTextArea: true },
    ]), []);

    const updateState = (key, value) => {
        setNotificationData(prev => ({ ...prev, [key]: value }))
    }

    const handleScheduleToggle = (isChecked) => {
        setNotificationData(prev => ({
            ...prev,
            scheduled_notification: isChecked,
            scheduled_date: isChecked ? prev.scheduled_date : '',
            scheduled_time: isChecked ? prev.scheduled_time : '',
        }))
    }


    return (
        <div className="w-full mx-auto p-6 bg-white shadow-md rounded-md">
            <h2 className="text-xl font-bold mb-4">{formTitle}</h2>
            <p className="mb-2">Add time that highlight what makes it unique</p>
            <p className="mb-6">Event Time</p>

            <div className="flex flex-col">
                {content?.map((item, index) => (
                    <div className="w-1/2 px-2 my-6" key={index}>
                        <div className='flex items-center justify-between'>
                            <label className="text-lg text-gray-700">{item?.label}</label>
                            <div className={`w-[450px] ${item?.isTextArea ? 'h-[150px]' : 'h-[56px]'} -mt-4`}>
                                <InputField
                                    value={notificationData?.[item?.value]}
                                    onChange={(value) => updateState(item?.value, value)}
                                    textArea={item?.isTextArea}
                                    className={item?.isTextArea ? 'min-h-[140px]' : ''}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center gap-3 px-2 my-6">
                <input
                    id="schedule-notification"
                    type="checkbox"
                    checked={notificationData?.scheduled_notification}
                    disabled={disableScheduleToggle}
                    onChange={(e) => handleScheduleToggle(e.target.checked)}
                    className={`w-5 h-5 accent-brandRed ${disableScheduleToggle ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                />
                <label htmlFor="schedule-notification" className="text-lg text-gray-700 cursor-pointer">
                    Schedule Notification
                </label>
            </div>

            {notificationData?.scheduled_notification && (
                <div className="flex flex-col sm:flex-row gap-6 px-2 my-6">
                    <div className="flex flex-col gap-2">
                        <label className="text-lg text-gray-700">Schedule Date</label>
                        <div className="w-[250px]">
                            <InputField
                                type="date"
                                value={notificationData?.scheduled_date}
                                onChange={(value) => updateState('scheduled_date', value)}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-lg text-gray-700">Schedule Time</label>
                        <div className="w-[250px]">
                            <InputField
                                type="time"
                                value={notificationData?.scheduled_time}
                                onChange={(value) => updateState('scheduled_time', value)}
                            />
                        </div>
                    </div>
                </div>
            )}


            <div className="flex justify-center mt-10 gap-5">
                <button
                    type="submit"
                    className="px-4 py-2 bg-brandRed text-white w-[120px] h-[50px]"
                    onClick={() => handleSubmit(notificationData, false)}
                >
                    {submitButtonLabel}
                </button>
                {cancel &&
                    <button type="button" className="px-4 py-2 bg-gray-200 text-gray-700 w-[120px] h-[50px]" onClick={handleCancel}>Cancel</button>
                }
                {allUser &&
                    <button
                        type="submit"
                        className="py-2  bg-green-500 text-white w-[150px] h-[50px]"
                        onClick={() => handleSubmit(notificationData, true)}
                    >
                        {allUsersButtonLabel}
                    </button>
                }
            </div>
        </div >
    );
};

export default Index;
